import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { useSyncSystem } from './sync-system-context';
import { onLocalReady } from './sync-event-bus';

const SOFT_LIMIT = 5 * 1024 * 1024; // 5 MB
const HARD_LIMIT = 10 * 1024 * 1024; // 10 MB


export type DocSizeLevel = 'ok' | 'warn' | 'error';

export interface DocSize {
  bytes: number;
  level: DocSizeLevel;
}

export function measure(doc: Y.Doc): number {
  return Y.encodeStateAsUpdateV2(doc).length;
}

function toLevel(bytes: number): DocSizeLevel {
  if (bytes >= HARD_LIMIT) return 'error';
  if (bytes >= SOFT_LIMIT) return 'warn';
  return 'ok';
}

export function useDocSize(
  entityId: string,
  measureFn: (doc: Y.Doc) => number = measure,
): DocSize {
  const { registry } = useSyncSystem();

  // Store measureFn in a ref so it never needs to be in the effect dep array.
  // This prevents infinite re-renders when the caller passes an inline function.
  const measureFnRef = useRef(measureFn);
  measureFnRef.current = measureFn;

  const [size, setSize] = useState<DocSize>(() => {
    const doc = registry.getDoc(entityId);
    if (!doc) return { bytes: 0, level: 'ok' };
    const bytes = measureFn(doc);
    return { bytes, level: toLevel(bytes) };
  });

  useEffect(() => {
    const doc = registry.getDoc(entityId);

    function subscribe(d: Y.Doc) {
      const bytes = measureFnRef.current(d);
      setSize({ bytes, level: toLevel(bytes) });
      const handler = () => {
        const b = measureFnRef.current(d);
        setSize({ bytes: b, level: toLevel(b) });
      };
      d.on('updateV2', handler);
      return () => d.off('updateV2', handler);
    }

    if (!doc) {
      setSize({ bytes: 0, level: 'ok' });
      // Doc not loaded yet — wait for localReady then subscribe
      return onLocalReady((id) => {
        if (id !== entityId) return;
        const d = registry.getDoc(entityId);
        if (d) subscribe(d);
      });
    }

    return subscribe(doc);
  }, [registry, entityId]);

  return size;
}
